import { useCallback, useEffect, useRef, useState } from 'react';
import {
  duitNowPayFields,
  type DuitNowFlow,
  type DuitNowSelection
} from '../../../constants/duitnow.const';
import { ayeApi } from '../../../core';
import type {
  CardBrand,
  EWalletBrand,
  PaymentMethodBrand
} from '../../../types/registration-payment.types';
import { submitPostForm } from '../../../utils/submit-post-form';
import { getUserErrorMessage } from '../../../utils/user-error-message';
import {
  GATEWAY_REQUIRES_IMPORTANT_NOTES,
  PAYMENT_METHOD_CONFIG
} from '../../register/RegisterScreen/constants/payment-method.const';

/** The DuitNow ID selection branch of DuitNowSelection, used by confirmDuitNowId. */
type DuitNowIdSelection = Extract<DuitNowSelection, { duitnow: 'id' }>;

export type AddPaymentStep =
  | 'select-payment-type'
  | 'select-card-brand'
  | 'select-ewallet-brand'
  | 'select-duitnow-method'
  | 'select-duitnow-bank'
  | 'select-duitnow-id'
  | 'duitnow-approval';

interface UseAddPaymentFlowOptions {
  isEWalletEnabled: boolean;
  /** Offer the DuitNow ID (decoupled) flow. When false, choosing DuitNow skips the method
   * screen and lands directly on bank selection. */
  isDuitNowIdEnabled: boolean;
  /** Called whenever a request creates a consent (even a stale/cancelled one), so the
   * caller can re-fetch the payment-methods list and show the new PENDING card. */
  onConsentPending?: (consentRef: string) => void;
}

export interface AddPaymentFlowState {
  step: AddPaymentStep;
  selectedBrand: PaymentMethodBrand | null;
  showImportantNotes: boolean;
  isRedirecting: boolean;
  errorMessage: string | undefined;
  /** Which chrome the error should use. 'neutral' (the "couldn't find that request") must
   * not show FailedPopup's red "Failed" heading/failure image — nothing has actually failed. */
  errorVariant: 'failed' | 'neutral';
  pendingConsentRef?: string;
  /** Whether pendingConsentRef came from this session's own /pay call ('fresh' — the
   * consent is known to exist even if the list doesn't show it yet, pre backend Part D) or from
   * a resumed `?duitnow_ref=` link ('resumed' — could be stale/foreign/already-gone). */
  pendingConsentSource: 'fresh' | 'resumed';
  /** The last DuitNowFlow choice on Screen 2, so coming back from Screen 3 re-selects it
   * instead of resetting to the default 'consent'. */
  lastDuitNowFlow: DuitNowFlow;
  /** A /pay failure on a DuitNow step (bank or ID) surfaces here instead of `errorMessage`, so
   * that step stays mounted with its selection/value kept and the backend's message inline. */
  duitNowStepError?: string;
  /** The DuitNow steps' own in-flight flag. It must NOT reuse `isRedirecting`, whose
   * full-screen "Redirecting…" spinner would unmount the step (resetting its selection or
   * typed value) while the request is in flight. This only disables the step's Continue button. */
  isSubmittingDuitNowStep: boolean;
  /** The bank last picked on the bank step, kept while the dialog is open so returning to the
   * step re-selects it. */
  lastDuitNowBankCode?: string;
}

export interface AddPaymentFlowHandlers {
  step: AddPaymentStep;
  selectedBrand: PaymentMethodBrand | null;
  showImportantNotes: boolean;
  isRedirecting: boolean;
  isResolvingGateway: boolean;
  errorMessage: string | undefined;
  errorVariant: 'failed' | 'neutral';
  pendingConsentRef?: string;
  pendingConsentSource: 'fresh' | 'resumed';
  lastDuitNowFlow: DuitNowFlow;
  duitNowStepError?: string;
  isSubmittingDuitNowStep: boolean;
  lastDuitNowBankCode: string | undefined;
  goToTypeSelection: () => void;
  goToCardBrandSelection: () => void;
  goToEWalletBrandSelection: () => void;
  goToDuitNowMethodSelection: () => void;
  confirmCardBrand: (brand: CardBrand) => void;
  confirmEWalletBrand: (brand: EWalletBrand) => void;
  confirmDuitNowMethod: (flow: DuitNowFlow) => void;
  confirmDuitNowBank: (bankCode: string) => void;
  rememberDuitNowBank: (bankCode: string) => void;
  confirmDuitNowId: (sel: DuitNowIdSelection) => void;
  resumeDuitNowApproval: (ref: string) => void;
  closeImportantNotes: () => void;
  doAddPayment: () => Promise<void>;
  setErrorMessage: (message: string, variant?: 'failed' | 'neutral') => void;
  reset: () => void;
}

const INITIAL_STATE: AddPaymentFlowState = {
  step: 'select-payment-type',
  selectedBrand: null,
  showImportantNotes: false,
  isRedirecting: false,
  errorMessage: undefined,
  errorVariant: 'failed',
  pendingConsentRef: undefined,
  pendingConsentSource: 'fresh',
  lastDuitNowFlow: 'consent',
  duitNowStepError: undefined,
  isSubmittingDuitNowStep: false,
  lastDuitNowBankCode: undefined
};

export function useAddPaymentFlow({
  isEWalletEnabled: _isEWalletEnabled,
  isDuitNowIdEnabled,
  onConsentPending
}: UseAddPaymentFlowOptions): AddPaymentFlowHandlers {
  const [state, setState] = useState<AddPaymentFlowState>(INITIAL_STATE);
  const [isResolvingGateway, setIsResolvingGateway] = useState(false);
  // Ignore a reentrant _executePayment call (double tap, Enter + click) while one is
  // already in flight.
  const isExecutingRef = useRef(false);
  // Bumped on every new attempt and on reset()/bfcache-restore. A request whose generation
  // has gone stale by the time it resolves must not navigate or touch visible state.
  const requestGenRef = useRef(0);

  const goToTypeSelection = useCallback(() => {
    setState((prev) => ({
      ...prev,
      step: 'select-payment-type',
      selectedBrand: null
    }));
  }, []);

  const goToCardBrandSelection = useCallback(() => {
    setState((prev) => ({ ...prev, step: 'select-card-brand' }));
  }, []);

  const goToEWalletBrandSelection = useCallback(() => {
    setState((prev) => ({ ...prev, step: 'select-ewallet-brand' }));
  }, []);

  const goToDuitNowMethodSelection = useCallback(() => {
    setState((prev) =>
      isDuitNowIdEnabled
        ? { ...prev, step: 'select-duitnow-method' }
        : {
            ...prev,
            step: 'select-duitnow-bank',
            duitNowStepError: undefined,
            lastDuitNowFlow: 'consent'
          }
    );
  }, [isDuitNowIdEnabled]);

  const _executePayment = useCallback(
    async (selection: PaymentMethodBrand | DuitNowSelection) => {
      if (isExecutingRef.current) return; // double tap / Enter+click — ignore the re-entry
      isExecutingRef.current = true;
      const myGen = ++requestGenRef.current;

      const duitNowSelection =
        typeof selection === 'object' ? selection : undefined;
      const brand = typeof selection === 'string' ? selection : undefined;
      // A /pay failure on a DuitNow step (bank or ID) must stay on that step with the message
      // inline (selection kept), not take over the whole dialog via the generic errorMessage panel.
      const isDuitNowStepFlow = duitNowSelection !== undefined;
      const fallbackMessage =
        'Sorry, something went wrong while processing your request. Please try again.';

      setState((prev) => ({
        ...prev,
        showImportantNotes: false,
        isRedirecting: !isDuitNowStepFlow,
        isSubmittingDuitNowStep: isDuitNowStepFlow,
        errorMessage: undefined,
        duitNowStepError: undefined
      }));

      try {
        const response = await ayeApi.generatePayUrl({
          body: {
            destination: 'home',
            ...duitNowPayFields(duitNowSelection),
            payment_method: duitNowSelection
              ? duitNowPayFields(duitNowSelection).payment_method
              : brand
          }
        });

        // The dialog was closed (or a newer attempt started) while this request was in
        // flight — never navigate or surface state for a stale attempt. A consent that DID get
        // created should still be surfaced so the payment-methods list picks up the PENDING row.
        if (requestGenRef.current !== myGen) {
          if (response.success && response.data.consentRef) {
            onConsentPending?.(response.data.consentRef);
          }
          return;
        }

        if (response.success) {
          const { payUrl, consentRef, consentForm } = response.data;
          if (consentForm) {
            submitPostForm(consentForm);
            return;
          }
          if (consentRef) {
            setState((p) => ({
              ...p,
              isRedirecting: false,
              isSubmittingDuitNowStep: false,
              step: 'duitnow-approval',
              pendingConsentRef: consentRef,
              pendingConsentSource: 'fresh' // created by /pay in this session
            }));
            onConsentPending?.(consentRef);
            return;
          }
          if (payUrl) window.open(payUrl, '_self');
        } else {
          const message = getUserErrorMessage(response, fallbackMessage);
          setState((prev) =>
            isDuitNowStepFlow
              ? {
                  ...prev,
                  isSubmittingDuitNowStep: false,
                  duitNowStepError: message
                }
              : { ...prev, isRedirecting: false, errorMessage: message }
          );
        }
      } catch {
        if (requestGenRef.current !== myGen) return; // stale — nothing to show
        setState((prev) =>
          isDuitNowStepFlow
            ? {
                ...prev,
                isSubmittingDuitNowStep: false,
                duitNowStepError: fallbackMessage
              }
            : { ...prev, isRedirecting: false, errorMessage: fallbackMessage }
        );
      } finally {
        isExecutingRef.current = false;
      }
    },
    [onConsentPending]
  );

  // Restored from the bfcache (browser Back from the bank's redirect page) with a stuck
  // "Redirecting…" spinner — clear it instead of leaving it frozen, and invalidate any in-flight
  // request from before the navigation.
  useEffect(() => {
    const onPageShow = (e: PageTransitionEvent) => {
      if (!e.persisted) return;
      requestGenRef.current += 1;
      isExecutingRef.current = false; // don't leave a future Continue silently swallowed
      setState((prev) =>
        prev.isRedirecting || prev.isSubmittingDuitNowStep
          ? INITIAL_STATE
          : prev
      );
      setIsResolvingGateway(false);
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  const confirmBrand = useCallback(
    async (brand: PaymentMethodBrand) => {
      setState((prev) => ({ ...prev, selectedBrand: brand }));
      setIsResolvingGateway(true);

      try {
        const response = await ayeApi.resolveGateway({
          queryParams: { payment_method: brand }
        });
        const requiresNotes =
          GATEWAY_REQUIRES_IMPORTANT_NOTES[
            (response as { data?: { gateway?: string } }).data?.gateway ?? ''
          ] ??
          PAYMENT_METHOD_CONFIG[brand]?.requiresImportantNotes ??
          false;

        setIsResolvingGateway(false);

        if (requiresNotes) {
          setState((prev) => ({ ...prev, showImportantNotes: true }));
        } else {
          await _executePayment(brand);
        }
      } catch {
        // Fallback to static config on API error
        const requiresNotes =
          PAYMENT_METHOD_CONFIG[brand]?.requiresImportantNotes ?? false;

        setIsResolvingGateway(false);

        if (requiresNotes) {
          setState((prev) => ({ ...prev, showImportantNotes: true }));
        } else {
          await _executePayment(brand);
        }
      }
    },
    [_executePayment]
  );

  const confirmCardBrand = useCallback(
    (brand: CardBrand) => confirmBrand(brand),
    [confirmBrand]
  );

  const confirmEWalletBrand = useCallback(
    (brand: EWalletBrand) => confirmBrand(brand),
    [confirmBrand]
  );

  // DuitNow skips resolveGateway and the Important Notes (RM 1.00) dialog entirely.
  const confirmDuitNowMethod = useCallback((flow: DuitNowFlow) => {
    setState((prev) => ({
      ...prev,
      step: flow === 'consent' ? 'select-duitnow-bank' : 'select-duitnow-id',
      duitNowStepError: undefined,
      lastDuitNowFlow: flow // the method screen re-selects this when coming back from the next screen
    }));
  }, []);

  const confirmDuitNowBank = useCallback(
    (bankCode: string) => {
      void _executePayment({ duitnow: 'consent', bankCode });
    },
    [_executePayment]
  );

  const rememberDuitNowBank = useCallback((bankCode: string) => {
    setState((prev) => ({ ...prev, lastDuitNowBankCode: bankCode }));
  }, []);

  const confirmDuitNowId = useCallback(
    (sel: DuitNowIdSelection) => {
      void _executePayment(sel);
    },
    [_executePayment]
  );

  const resumeDuitNowApproval = useCallback((ref: string) => {
    setState((prev) => ({
      ...prev,
      step: 'duitnow-approval',
      pendingConsentRef: ref,
      pendingConsentSource: 'resumed' // from ?duitnow_ref=, not necessarily this session's
    }));
  }, []);

  const closeImportantNotes = useCallback(() => {
    setState((prev) => ({ ...prev, showImportantNotes: false }));
  }, []);

  const doAddPayment = useCallback(async () => {
    if (state.selectedBrand) {
      await _executePayment(state.selectedBrand);
    }
  }, [_executePayment, state.selectedBrand]);

  const setErrorMessage = useCallback(
    (message: string, variant: 'failed' | 'neutral' = 'failed') => {
      setState((prev) => ({
        ...prev,
        errorMessage: message,
        errorVariant: variant
      }));
    },
    []
  );

  const reset = useCallback(() => {
    requestGenRef.current += 1; // invalidate any request from a previous attempt
    isExecutingRef.current = false; // a Continue after close/reopen must not be swallowed
    setState(INITIAL_STATE);
    setIsResolvingGateway(false);
  }, []);

  return {
    step: state.step,
    selectedBrand: state.selectedBrand,
    showImportantNotes: state.showImportantNotes,
    isRedirecting: state.isRedirecting,
    isResolvingGateway,
    errorMessage: state.errorMessage,
    errorVariant: state.errorVariant,
    pendingConsentRef: state.pendingConsentRef,
    pendingConsentSource: state.pendingConsentSource,
    lastDuitNowFlow: state.lastDuitNowFlow,
    duitNowStepError: state.duitNowStepError,
    isSubmittingDuitNowStep: state.isSubmittingDuitNowStep,
    lastDuitNowBankCode: state.lastDuitNowBankCode,
    goToTypeSelection,
    goToCardBrandSelection,
    goToEWalletBrandSelection,
    goToDuitNowMethodSelection,
    confirmCardBrand,
    confirmEWalletBrand,
    confirmDuitNowMethod,
    confirmDuitNowBank,
    rememberDuitNowBank,
    confirmDuitNowId,
    resumeDuitNowApproval,
    closeImportantNotes,
    doAddPayment,
    setErrorMessage,
    reset
  };
}
